import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTools from "@/components/feature/ScrollTools";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { evidenceLevels, glossary, methodSteps, misleadingTechniques, selfCheckList } from "@/mocks/site";

const jumpLinks = [
  { id: "steps", label: "四步核验法" },
  { id: "evidence", label: "证据等级" },
  { id: "techniques", label: "常见误导手法" },
  { id: "checklist", label: "自检清单" },
  { id: "glossary", label: "术语表" },
];

const toneClass: Record<string, { wrap: string; badge: string; label: string }> = {
  primary: { wrap: "border-primary-200 bg-primary-50/70", badge: "bg-primary-800 text-background-50", label: "text-primary-800" },
  secondary: {
    wrap: "border-secondary-200 bg-secondary-50/70",
    badge: "bg-secondary-500 text-background-50",
    label: "text-secondary-800",
  },
  accent: { wrap: "border-accent-200 bg-accent-50/70", badge: "bg-accent-500 text-accent-950", label: "text-accent-800" },
};

export default function Guide() {
  const [checked, setChecked] = useState<boolean[]>(() => selfCheckList.map(() => false));
  const doneCount = checked.filter(Boolean).length;

  useSeo({
    title: "观察指南｜四步核验法与常见误导手法 - 瓜田黑料",
    description:
      "瓜田黑料观察指南：一套可复用的四步核验法，教你区分说法与材料、识别时间错位与语境截断等误导手法，并提供证据等级划分、自检清单与舆论反转术语表。",
    keywords: "瓜田黑料观察指南,信息核验,证据等级,误导手法,舆论反转,自检清单",
    path: "/guide",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "四步核验法：判断一场风波是否发生反转",
        description: "一套可复用的信息核验流程，用于判断舆论事件的性质与方向。",
        totalTime: "PT10M",
        step: methodSteps.map((step, index) => ({
          "@type": "HowToStep",
          position: index + 1,
          name: step.title,
          text: step.desc,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://suntohome.cn/" },
          { "@type": "ListItem", position: 2, name: "观察指南", item: "https://suntohome.cn/guide" },
        ],
      },
    ],
  });

  const toggle = (index: number) => {
    setChecked((prev) => prev.map((value, i) => (i === index ? !value : value)));
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "观察指南" }]} />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-8">
                <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">METHODOLOGY</p>
                <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  观察指南：把情绪和事实分开
                </h1>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">
                  反转之所以频繁，是因为大多数判断建立在片段之上。这一页是我们内部在用的核验流程，
                  它不能替你得出结论，但能帮你知道自己的结论建立在什么材料之上。
                </p>
              </div>
              <nav aria-label="指南章节导航" className="lg:col-span-4">
                <ul className="flex flex-wrap gap-2 lg:justify-end">
                  {jumpLinks.map((link) => (
                    <li key={link.id}>
                      <a
                        href={`#${link.id}`}
                        className="block whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        </section>

        <section id="steps" className="w-full scroll-mt-32 border-b border-background-200 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <header className="max-w-3xl">
              <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">STEP BY STEP</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">四步核验法</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                这四步按顺序走，任何一步走不通，结论都应该停在「待核实」。
              </p>
            </header>

            <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              {methodSteps.map((step) => (
                <li
                  key={step.no}
                  className="flex gap-5 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-950 font-heading text-sm font-bold text-background-50">
                    {step.no}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="evidence" className="w-full scroll-mt-32 border-b border-background-200 bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <header className="max-w-3xl">
              <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">EVIDENCE LEVEL</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">证据等级怎么分</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们不对信息作价值判断，只对它的可核验程度分级。等级越高，越适合作为结论的依据。
              </p>
            </header>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {evidenceLevels.map((item) => {
                const tone = toneClass[item.tone] ?? toneClass.primary;
                return (
                  <article key={item.level} className={`flex flex-col rounded-lg border p-5 md:p-6 ${tone.wrap}`}>
                    <span
                      className={`inline-flex h-9 w-fit items-center justify-center rounded-md px-3 font-heading text-xs font-bold ${tone.badge}`}
                    >
                      {item.level}
                    </span>
                    <h3 className={`mt-4 font-heading text-base font-semibold ${tone.label}`}>{item.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="techniques" className="w-full scroll-mt-32 border-b border-background-200 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <header className="lg:col-span-4">
                <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">COMMON TRICKS</p>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  五种常见误导手法
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  这些手法本身不带立场，它们只是让材料看起来比实际更有说服力。识别它们不需要专业知识，
                  只需要多问一句「我看到的完整版本在哪里」。
                </p>
              </header>

              <ul className="divide-y divide-background-200 border-y border-background-200 lg:col-span-8">
                {misleadingTechniques.map((item, index) => (
                  <li key={item.name} className="flex items-start gap-5 py-5">
                    <span className="font-heading text-lg font-bold text-background-400">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{item.name}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="checklist" className="w-full scroll-mt-32 border-b border-background-200 bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-5">
                <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">SELF CHECK</p>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">转发前的自检清单</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  下次准备把一条消息转出去之前，花三十秒过一遍这五条。全部勾上再转，你的时间线会安静很多。
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-background-300">
                      <div
                        className="h-2 rounded-full bg-primary-700 transition-[width] duration-300"
                        style={{ width: `${(doneCount / selfCheckList.length) * 100}%` }}
                        aria-hidden="true"
                      />
                    </div>
                    <p className="mt-2 text-[11px] text-foreground-500">
                      已完成 {doneCount} / {selfCheckList.length} 项
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setChecked(selfCheckList.map(() => false))}
                    className="whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    重置
                  </button>
                </div>
              </div>

              <ul className="flex flex-col gap-3 lg:col-span-7">
                {selfCheckList.map((item, index) => (
                  <li key={item}>
                    <label
                      htmlFor={`check-${index}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3.5 transition-colors ${
                        checked[index]
                          ? "border-primary-300 bg-primary-50/70"
                          : "border-background-300 bg-background-50 hover:border-primary-300"
                      }`}
                    >
                      <input
                        id={`check-${index}`}
                        type="checkbox"
                        checked={checked[index]}
                        onChange={() => toggle(index)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-primary-700"
                      />
                      <span
                        className={`text-sm leading-relaxed ${
                          checked[index] ? "text-foreground-950 line-through decoration-primary-400" : "text-foreground-700"
                        }`}
                      >
                        {item}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="glossary" className="w-full scroll-mt-32 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <header className="max-w-3xl">
              <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">GLOSSARY</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">术语表</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                站内反复出现的说法，在这里统一定义，避免读的时候各理解各的。
              </p>
            </header>

            <dl className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-2 lg:grid-cols-3">
              {glossary.map((item) => (
                <div key={item.term} className="bg-background-50 p-5">
                  <dt className="font-heading text-sm font-semibold text-foreground-950">{item.term}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:p-8">
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-950">看一遍真实案例，比读十遍方法管用</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  案例库里每份档案都按这套流程整理，可以直接对照着看。
                </p>
              </div>
              <Link
                to="/cases"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-archive-2-line text-base" aria-hidden="true" />
                进入案例库
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollTools />
    </div>
  );
}